import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { getFeedback } from '@/api/feedback';
import { useTranslation } from '@/i18n';
import { FeedbackPanel } from './FeedbackPanel';

interface FeedbackPromptProps {
    accessToken: string;
}

/**
 * Whatever this restaurant does about feedback, at the bottom of the receipt.
 *
 * Three outcomes, and the app switches on the server's `presentation` rather than on a vendor name. A client
 * that branches on vendors needs a release every time a restaurant changes one, and the mapping is the
 * server's to own — this file should not know that Akira has a URL and Tattle has neither.
 *
 * Nothing renders while it loads and nothing renders if it fails. The prompt sits below a receipt the guest
 * came here for; a spinner or an error under it would suggest something went wrong with their payment, which
 * is the one thing this screen exists to say did not.
 */
export function FeedbackPrompt({ accessToken }: FeedbackPromptProps) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const feedback = useQuery({
        queryKey: ['feedback'],
        queryFn: ({ signal }) => getFeedback(accessToken, signal),
        // The guest has paid. Nothing below this point is worth making them wait on twice.
        retry: 1,
        staleTime: Infinity,
    });

    if (!feedback.data || dismissed) return null;
    const { presentation, campaign, surveyUrl } = feedback.data;

    if (presentation === 'ExternalLink' && surveyUrl) {
        return (
            <a
                href={surveyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center justify-center rounded-xl border border-border bg-background px-5 py-3 text-center text-[0.9375rem] font-medium text-foreground shadow-xs"
            >
                {t('receipt.feedback')}
                {/* It leaves the app, so it says so rather than surprising anyone. */}
                <span className="sr-only"> ({t('receipt.feedbackExternal')})</span>
            </a>
        );
    }

    if (presentation !== 'InApp' || !campaign) return null;

    if (!opened) {
        return (
            <button
                type="button"
                onClick={() => setOpened(true)}
                aria-expanded={false}
                className="inline-flex min-h-11 items-center justify-center rounded-xl border border-border bg-background px-5 py-3 text-center text-[0.9375rem] font-medium text-foreground shadow-xs"
            >
                {t('receipt.feedback')}
            </button>
        );
    }

    /*
     * Opened in place rather than navigated to.
     *
     * The receipt is not a route of its own — it is the terminal state of `/pay` — so a feedback route would
     * have to reconstruct a receipt it does not own. Expanding below it also leaves the receipt on screen,
     * which a guest may well still want, and leaves the browser's back button meaning what it meant a moment
     * ago, which on a phone is the control they trust most.
     */
    return (
        <FeedbackPanel campaign={campaign} accessToken={accessToken} onDismiss={() => setDismissed(true)} />
    );
}
